// Relative, not '@/': vite.config.ts reads measurementIdOf too.
import { readPreference, writePreference } from '../lib/preferences';

/**
 * Google Analytics, only after the visitor agrees. Until "Got it" on the
 * notice (consent-notice.ts) no Google script is loaded and no cookie is set;
 * the choice is kept on the device for 13 months, then asked again, and can be
 * changed on the privacy page. Ads features and Google signals stay off.
 *
 * Framework-free and small: the content pages load it on its own
 * (page-analytics.ts), the app through `analytics` (index.ts).
 */
export type AnalyticsChoice = 'granted' | 'denied';

/** `off`: this build or address doesn't measure at all (no ID, local, previews). */
export type AnalyticsState = AnalyticsChoice | 'unset' | 'off';

/** How long a choice (and the cookies it allows) lasts: 13 months, the usual ceiling for analytics consent. */
export const CONSENT_DAYS = 395;
const DAY_MS = 86_400_000;

/** Fired on `window` whenever the choice changes, so every surface (notice, privacy page) follows. */
export const ANALYTICS_EVENT = 'shycut:analytics';

const TAG_URL = 'https://www.googletagmanager.com/gtag/js';
const MEASUREMENT_ID = /^G-[A-Z0-9]{4,16}$/;
const GA_COOKIE = /^_ga($|_)|^_gid$/;

/** The configured ID when it looks like a GA4 measurement ID; anything else turns measuring off (it lands in a URL). */
export const measurementIdOf = (value: unknown): string | null => (typeof value === 'string' && MEASUREMENT_ID.test(value.trim()) ? value.trim() : null);

/** Stored as `<choice>:<epoch ms>`; null when missing, malformed, from the future or expired. */
export function parseChoice(raw: string | null, now: number): AnalyticsChoice | null {
  const match = raw?.match(/^(granted|denied):(\d{1,15})$/);
  if (!match) return null;
  const age = now - Number(match[2]);
  return age >= 0 && age < CONSENT_DAYS * DAY_MS ? (match[1] as AnalyticsChoice) : null;
}

/** `.example.com` and every parent of a host, where GA may have put its cookies. */
export const cookieDomains = (hostname: string): string[] => {
  const labels = hostname.split('.');
  return labels.slice(0, -1).map((_, index) => `.${labels.slice(index).join('.')}`);
};

/** Deletes Google Analytics cookies (`_ga`, `_ga_<id>`, `_gid`) on this host and every parent domain. */
export function clearAnalyticsCookies(doc: Document): void {
  const names = doc.cookie
    .split(';')
    .map((pair) => pair.split('=')[0]?.trim() ?? '')
    .filter((name) => GA_COOKIE.test(name));
  for (const name of names)
    for (const domain of ['', ...cookieDomains(doc.location.hostname)]) doc.cookie = `${name}=; Max-Age=0; Path=/${domain && `; Domain=${domain}`}`;
}

type TagWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void } & Record<`ga-disable-${string}`, boolean | undefined>;

export interface AnalyticsOptions {
  readonly measurementId: string | null;
  /** Addresses that are measured (the live site); anywhere else stays off. */
  readonly hosts: readonly string[];
  readonly win?: Window;
  readonly now?: () => number;
}

export interface Analytics {
  state(): AnalyticsState;
  /** Loads the tag when the visitor already agreed; safe to call more than once. */
  start(): void;
  /** "Got it": remembers the choice and starts measuring. */
  grant(): void;
  /** Stops measuring at once and deletes the cookies. */
  deny(): void;
}

export function createAnalytics({ measurementId, hosts, win = window, now = Date.now }: AnalyticsOptions): Analytics {
  const id = measurementIdOf(measurementId);
  const tagWindow = win as TagWindow;
  const enabled = id !== null && hosts.includes(win.location.hostname);

  const state = (): AnalyticsState => (enabled ? (parseChoice(readPreference('analytics'), now()) ?? 'unset') : 'off');

  const choose = (choice: AnalyticsChoice) => {
    writePreference('analytics', `${choice}:${now()}`);
    win.dispatchEvent(new CustomEvent(ANALYTICS_EVENT, { detail: choice }));
  };

  const load = (tagId: string) => {
    tagWindow[`ga-disable-${tagId}`] = false;
    // A content page that turned into the app has loaded it already.
    if (tagWindow.gtag) return;
    const dataLayer = (tagWindow.dataLayer ??= []);
    // gtag.js reads `arguments` objects from the queue, not arrays.
    tagWindow.gtag = function gtag() {
      dataLayer.push(arguments);
    };
    const gtag = tagWindow.gtag;
    gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    gtag('js', new Date());
    gtag('config', tagId, {
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      // Expires with the consent instead of being pushed back on every visit.
      cookie_expires: CONSENT_DAYS * 86_400,
      cookie_update: false,
      cookie_flags: 'SameSite=Lax;Secure',
    });
    const script = win.document.createElement('script');
    script.async = true;
    script.src = `${TAG_URL}?id=${encodeURIComponent(tagId)}`;
    win.document.head.append(script);
  };

  return {
    state,
    start: () => {
      if (id && state() === 'granted') load(id);
    },
    grant: () => {
      if (!id || !enabled) return;
      choose('granted');
      load(id);
    },
    deny: () => {
      if (!id || !enabled) return;
      choose('denied');
      tagWindow[`ga-disable-${id}`] = true;
      clearAnalyticsCookies(win.document);
    },
  };
}
